import React, { useCallback, useState } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl-next';

import { cssMap } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import CrossIcon from '@atlaskit/icon/core/cross';
import { IconButton } from '@atlaskit/button/new';
import Image from '@atlaskit/image';
import { Box, Flex } from '@atlaskit/primitives/compiled';
import { media } from '@atlaskit/primitives/responsive';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { xcss, Grid } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';

import GettingStartedCard from '@atlassian/getting-started-widget';

import { ConfluencePageSubType } from '@confluence/create-blank-fabric-page/entry-points/CreateBlankPageMutation.ts';
import { buildCreationLink } from '@confluence/create-blank-fabric-page/entry-points/createContentRouteUtils.ts';
import { useSetOnboardingState } from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState.ts';
import type { onboardingStateGetOnboardingStateQuery$data } from '@confluence/onboarding-helpers/entry-points/onboardingStateGetOnboardingStateQuery.ts';
import { PERSONALIZED_TEMPLATES_V2_ONBOARDING_STATE_KEYS } from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants.ts';
import { expVal } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';

import { usePTV2State } from './usePTV2Store.tsx';
import ImportWork from './assets/ImportWork.svg';
import ImportWorkDark from './assets/ImportWorkDark.svg';
import SearchApps from './assets/SearchApps.svg';
import SearchAppsDark from './assets/SearchAppsDark.svg';
import { usePersonalizedTemplateOnboardingActions } from './hooks/usePersonalizedTemplateOnboardingActions.tsx';
import { CreateTeamModal } from './CreateTeamModal.tsx';
import { useCreateSpaceDrawer } from './hooks/useCreateSpaceDrawer.tsx';
import type { GettingStartedCardType } from './PersonalizedTemplatesV2Types.ts';
import { useImportContentStoreActions } from '@confluence/import-content/entry-points/importContent.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

type HomeOnboardingActionCardsProps = {
	onboardingStateData?: onboardingStateGetOnboardingStateQuery$data;
};

export const i18n = defineMessages({
	heading: {
		id: 'experiment-personalized-templates-v2.home-onboarding-action-cards.heading',
		defaultMessage: 'Get started with Confluence',
		description:
			'This is the heading shown above the row of action cards that help users get started with Confluence. Appears on the home page to guide new users through initial setup tasks.',
	},
	createPage: {
		id: 'experiment-personalized-templates-v2.create.page',
		defaultMessage: 'Create a page',
		description:
			'This is the heading for the "Create a page" onboarding card. It appears as one of the action cards on the home onboarding panel to guide users in creating their first page.',
	},
	createPageDescription: {
		id: 'experiment-personalized-templates-v2.create.page.description',
		defaultMessage: 'Plan projects, take notes, and track tasks.',
		description:
			'This is the descriptive subtitle for the "Create a page" onboarding card explaining the benefits users can get from creating pages in Confluence.',
	},
	createPageCTA: {
		id: 'experiment-personalized-templates-v2.create.page.cta',
		defaultMessage: 'Create page',
		description:
			'This is the text on the call-to-action button for the "Create a page" onboarding card. When clicked, it initiates the page creation flow.',
	},
	setUpTeamSpace: {
		id: 'experiment-personalized-templates-v2.setup.team.space',
		defaultMessage: 'Set up a team space',
		description:
			'This is the heading for the "Set up a team space" onboarding card. It appears as one of the action cards to guide users in creating their first collaborative space.',
	},
	setUpTeamSpaceDescription: {
		id: 'experiment-personalized-templates-v2.setup.team.space.description',
		defaultMessage: 'Create a single source of truth for your team’s projects and docs.',
		description:
			'This is the descriptive subtitle for the "Set up a team space" onboarding card explaining the benefit of creating a shared space for team collaboration.',
	},
	setUpTeamSpaceCTA: {
		id: 'experiment-personalized-templates-v2.setup.team.space.cta',
		defaultMessage: 'Set up space',
		description:
			'This is the text on the call-to-action button for the "Set up a team space" onboarding card. When clicked, it initiates the team space creation flow.',
	},
	brainstormOnWhiteboard: {
		id: 'experiment-personalized-templates-v2.brainstorm.on.whiteboard',
		defaultMessage: 'Brainstorm on a whiteboard',
		description:
			'This is the heading for the "Brainstorm on a whiteboard" onboarding card. It appears as one of the action cards to guide users in creating whiteboards for visual collaboration.',
	},
	brainstormOnWhiteboardDescription: {
		id: 'experiment-personalized-templates-v2.brainstorm.on.whiteboard.description',
		defaultMessage: 'Dream up new ideas and visualize your work.',
		description:
			'This is the descriptive subtitle for the "Brainstorm on a whiteboard" onboarding card explaining the benefits of using whiteboards for visual collaboration.',
	},
	brainstormOnWhiteboardCTA: {
		id: 'experiment-personalized-templates-v2.brainstorm.on.whiteboard.cta',
		defaultMessage: 'Create whiteboard',
		description:
			'This is the text on the call-to-action button for the "Brainstorm on a whiteboard" onboarding card. When clicked, it initiates the whiteboard creation flow.',
	},
	createTeamProfile: {
		id: 'experiment-personalized-templates-v2.create.team.profile',
		defaultMessage: 'Create a team profile',
		description:
			'This is the heading for the "Create a team profile" onboarding card. It appears as one of the action cards to guide users in setting up a team profile.',
	},
	createTeamProfileDescription: {
		id: 'experiment-personalized-templates-v2.create.team.profile.description',
		defaultMessage: 'Bring everyone together with one team you can assign work to.',
		description:
			'This is the descriptive subtitle for the "Create a team profile" onboarding card explaining the benefit of setting up a team for work management.',
	},
	createTeamProfileCTA: {
		id: 'experiment-personalized-templates-v2.create.team.profile.cta',
		defaultMessage: 'Create team',
		description:
			'This is the text on the call-to-action button for the "Create a team profile" onboarding card. When clicked, it initiates the team profile creation flow.',
	},
	completedCTAText: {
		id: 'experiment-personalized-templates-v2.completed.cta',
		defaultMessage: 'Completed',
		description:
			'This is the text displayed on the button of an onboarding action card after the user has successfully completed the associated action.',
	},
	dismissLabel: {
		id: 'experiment-personalized-templates-v2.home-onboarding-action-cards.dismiss.label',
		defaultMessage: 'Dismiss',
		description:
			'This is the accessible label for the dismiss icon button on individual onboarding action cards. Used by screen readers to announce the button purpose.',
	},
	dismissOnboardingLabel: {
		id: 'experiment-personalized-templates-v2.dismiss.onboarding.label',
		defaultMessage: 'Dismiss onboarding',
		description:
			'Label on dismiss close icon button for the onboarding cards row of the personalized templates experiment. When clicked the row of different onboarding actions is dismissed.',
	},
});

export const i18nCfind5225 = defineMessages({
	heading: {
		id: 'experiment-personalized-templates-v2.home-onboarding-action-cards-v2.heading',
		defaultMessage: 'Start creating your knowledge base',
		description:
			'This is the heading shown above the row of updated action cards in the v2 experiment. It guides users toward building their organizational knowledge base.',
	},
	importWork: {
		id: 'experiment-personalized-templates-v2.import-work',
		defaultMessage: 'Import work',
		description:
			'This is the heading for the "Import work" onboarding card in the v2 experiment. It guides users in importing existing work from external sources.',
	},
	importWorkDescription: {
		id: 'experiment-personalized-templates-v2.import-work.description',
		defaultMessage: 'Consolidate existing work from Google Drive, Notion, Miro, and more.',
		description:
			'This is the descriptive subtitle for the "Import work" onboarding card explaining the benefit of consolidating work from various external sources into Confluence.',
	},
	importWorkCTA: {
		id: 'experiment-personalized-templates-v2.import-work.cta',
		defaultMessage: 'Start import',
		description:
			'This is the text on the call-to-action button for the "Import work" onboarding card. When clicked, it initiates the work import flow.',
	},
	searchApps: {
		id: 'experiment-personalized-templates-v2.search-apps',
		defaultMessage: 'Search across apps',
		description:
			'Heading on the "Search across apps" onboarding action card. This card appears as one of the action cards on the home onboarding panel to guide users in using Rovo for searching and accessing organizational knowledge.',
	},
	searchAppsDescription: {
		id: 'experiment-personalized-templates-v2.search-apps.description',
		defaultMessage: 'Easily access and share org-wide knowledge with Rovo.',
		description:
			'This is the descriptive subtitle for the "Search across apps" onboarding card explaining the benefit of using Rovo for accessing organizational knowledge.',
	},
	searchAppsCTA: {
		id: 'experiment-personalized-templates-v2.search-apps.cta',
		defaultMessage: 'Connect apps',
		description:
			'This is the text on the call-to-action button for the "Search across apps" onboarding card. When clicked, it allows users to connect external apps to Rovo.',
	},
	draftIdeas: {
		id: 'experiment-personalized-templates-v2.draft-ideas',
		defaultMessage: 'Draft ideas in docs',
		description:
			'This is the heading for the "Draft ideas in docs" onboarding card. It guides users in creating documents for brainstorming and idea generation.',
	},
	draftIdeasDescription: {
		id: 'experiment-personalized-templates-v2.draft-ideas.description',
		defaultMessage: 'Plan projects, take notes, and track tasks.',
		description:
			'This is the descriptive subtitle for the "Draft ideas in docs" onboarding card explaining the uses of documents for planning and tracking work.',
	},
	draftIdeasCTA: {
		id: 'experiment-personalized-templates-v2.draft-ideas.cta',
		defaultMessage: 'Create doc',
		description:
			'Text on the call-to-action button for the "Draft ideas in docs" onboarding card. When clicked, it initiates the document creation flow.',
	},
});

const noop = () => {};

const styles = cssMap({
	containerStyles: {
		paddingBottom: token('space.300'),
	},
	headerStyles: {
		alignItems: 'center',
		paddingBottom: token('space.150'),
	},
	imageStyles: {
		width: '100%',
		objectFit: 'cover',
	},
});

const gridStyles = xcss({
	gridTemplateColumns: 'repeat(2, 1fr)',
	[media.above.md]: { gridTemplateColumns: 'repeat(4, 1fr)' },
});

type CreateContentCTAProps = {
	contentType: 'page' | 'whiteboard';
	source: string;
	personalSpaceKey: string;
	openTemplatesGallery?: boolean;
};

const CDN_IMAGES = {
	pageImage: {
		component: (
			<Image
				src="https://dam-cdn.atl.orangelogic.com/CDNLink/AT12EFL4.svg"
				srcDark="https://dam-cdn.atl.orangelogic.com/CDNLink/AT12EFL8.svg"
				alt=""
				// eslint-disable-next-line @atlaskit/design-system/no-unsafe-style-overrides
				css={styles.imageStyles}
			/>
		),
	},
	setupSpace: {
		component: (
			<Image
				src="https://dam-cdn.atl.orangelogic.com/CDNLink/AT12EFL3.svg"
				srcDark="https://dam-cdn.atl.orangelogic.com/CDNLink/AT12EFL1.svg"
				alt=""
				// eslint-disable-next-line @atlaskit/design-system/no-unsafe-style-overrides
				css={styles.imageStyles}
			/>
		),
	},
	brainstormWhiteboard: {
		component: (
			<Image
				src="https://dam-cdn.atl.orangelogic.com/CDNLink/AT12EFL5.svg"
				srcDark="https://dam-cdn.atl.orangelogic.com/CDNLink/AT12EFL2.svg"
				alt=""
				// eslint-disable-next-line @atlaskit/design-system/no-unsafe-style-overrides
				css={styles.imageStyles}
			/>
		),
	},
	teamProfile: {
		component: (
			<Image
				src="https://dam-cdn.atl.orangelogic.com/CDNLink/AT12EFL6.svg"
				srcDark="https://dam-cdn.atl.orangelogic.com/CDNLink/AT12EFL7.svg"
				alt=""
				// eslint-disable-next-line @atlaskit/design-system/no-unsafe-style-overrides
				css={styles.imageStyles}
			/>
		),
	},
};

export const getOnboardingActionsData = (): GettingStartedCardType[] => {
	return [
		{
			id: 'createPage',
			image: CDN_IMAGES.pageImage,
			heading: <FormattedMessage {...i18n.createPage} />,
			description: <FormattedMessage {...i18n.createPageDescription} />,
			ctaText: <FormattedMessage {...i18n.createPageCTA} />,
			ctaCompletedText: <FormattedMessage {...i18n.completedCTAText} />,
			dismissTooltip: <FormattedMessage {...i18n.dismissLabel} />,
			onDismiss: noop,
		},
		{
			id: 'setupTeamSpace',
			image: CDN_IMAGES.setupSpace,
			heading: <FormattedMessage {...i18n.setUpTeamSpace} />,
			description: <FormattedMessage {...i18n.setUpTeamSpaceDescription} />,
			ctaText: <FormattedMessage {...i18n.setUpTeamSpaceCTA} />,
			ctaCompletedText: <FormattedMessage {...i18n.completedCTAText} />,
			dismissTooltip: <FormattedMessage {...i18n.dismissLabel} />,
			onDismiss: noop,
		},
		{
			id: 'brainstormOnWhiteboard',
			image: CDN_IMAGES.brainstormWhiteboard,
			heading: <FormattedMessage {...i18n.brainstormOnWhiteboard} />,
			description: <FormattedMessage {...i18n.brainstormOnWhiteboardDescription} />,
			ctaText: <FormattedMessage {...i18n.brainstormOnWhiteboardCTA} />,
			ctaCompletedText: <FormattedMessage {...i18n.completedCTAText} />,
			dismissTooltip: <FormattedMessage {...i18n.dismissLabel} />,
			onDismiss: noop,
		},
		{
			id: 'createTeamProfile',
			image: CDN_IMAGES.teamProfile,
			heading: <FormattedMessage {...i18n.createTeamProfile} />,
			description: <FormattedMessage {...i18n.createTeamProfileDescription} />,
			ctaText: <FormattedMessage {...i18n.createTeamProfileCTA} />,
			ctaCompletedText: <FormattedMessage {...i18n.completedCTAText} />,
			dismissTooltip: <FormattedMessage {...i18n.dismissLabel} />,
			onDismiss: noop,
		},
	];
};

export const OnboardingActionCardsCFIND5225: GettingStartedCardType[] = [
	{
		id: 'setupTeamSpace',
		image: CDN_IMAGES.setupSpace,
		heading: <FormattedMessage {...i18n.setUpTeamSpace} />,
		description: <FormattedMessage {...i18n.setUpTeamSpaceDescription} />,
		ctaText: <FormattedMessage {...i18n.setUpTeamSpaceCTA} />,
		ctaCompletedText: <FormattedMessage {...i18n.completedCTAText} />,
		dismissTooltip: <FormattedMessage {...i18n.dismissLabel} />,
		onDismiss: noop,
	},
	{
		id: 'importWork',
		image: { src: ImportWork, srcDark: ImportWorkDark },
		heading: <FormattedMessage {...i18nCfind5225.importWork} />,
		description: <FormattedMessage {...i18nCfind5225.importWorkDescription} />,
		ctaText: <FormattedMessage {...i18nCfind5225.importWorkCTA} />,
		ctaCompletedText: <FormattedMessage {...i18n.completedCTAText} />,
		dismissTooltip: <FormattedMessage {...i18n.dismissLabel} />,
		onDismiss: noop,
	},
	{
		id: 'searchApps',
		image: { src: SearchApps, srcDark: SearchAppsDark },
		heading: <FormattedMessage {...i18nCfind5225.searchApps} />,
		description: <FormattedMessage {...i18nCfind5225.searchAppsDescription} />,
		ctaText: <FormattedMessage {...i18nCfind5225.searchAppsCTA} />,
		ctaCompletedText: <FormattedMessage {...i18n.completedCTAText} />,
		dismissTooltip: <FormattedMessage {...i18n.dismissLabel} />,
		onDismiss: noop,
	},
	{
		id: 'draftIdeas',
		image: CDN_IMAGES.pageImage,
		heading: <FormattedMessage {...i18nCfind5225.draftIdeas} />,
		description: <FormattedMessage {...i18nCfind5225.draftIdeasDescription} />,
		ctaText: <FormattedMessage {...i18nCfind5225.draftIdeasCTA} />,
		ctaCompletedText: <FormattedMessage {...i18n.completedCTAText} />,
		dismissTooltip: <FormattedMessage {...i18n.dismissLabel} />,
		onDismiss: noop,
	},
	{
		id: 'brainstormOnWhiteboard',
		image: CDN_IMAGES.brainstormWhiteboard,
		heading: <FormattedMessage {...i18n.brainstormOnWhiteboard} />,
		description: <FormattedMessage {...i18n.brainstormOnWhiteboardDescription} />,
		ctaText: <FormattedMessage {...i18n.brainstormOnWhiteboardCTA} />,
		ctaCompletedText: <FormattedMessage {...i18n.completedCTAText} />,
		dismissTooltip: <FormattedMessage {...i18n.dismissLabel} />,
		onDismiss: noop,
	},
];

const getButtonHref = ({
	contentType,
	source,
	personalSpaceKey,
	openTemplatesGallery,
}: CreateContentCTAProps): string => {
	return buildCreationLink({
		contentType,
		source,
		spaceKey: personalSpaceKey,
		subType: ConfluencePageSubType.LIVE,
		openTemplatesGallery: openTemplatesGallery || undefined,
	});
};

export const getRovoLink = (orgId?: string, environment?: string) => {
	if (!orgId || !environment) {
		return undefined;
	}

	if (orgId) {
		if (environment === 'PRODUCTION') {
			return `https://admin.atlassian.com/o/${orgId}/rovo?showStep=QUIZ_MODAL`;
		} else if (environment === 'STAGING') {
			return `https://admin.stg.atlassian.com/o/${orgId}/rovo?showStep=QUIZ_MODAL`;
		}
	}
};

export const HomeOnboardingActionCards = ({
	onboardingStateData,
}: HomeOnboardingActionCardsProps) => {
	const { setOnboardingState } = useSetOnboardingState();
	const { formatMessage } = useIntl();
	const { personalSpaceKey, onboardingPeriodDay, isXflow } = usePTV2State();
	const [isCreateTeamModalOpen, setCreateTeamModalOpen] = useState(false);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { handleSetupTeamSpaceClick, canCreateSpace } = useCreateSpaceDrawer();
	const importContentActions = useImportContentStoreActions();
	const { isRovoEnabled, isRovoLLMEnabled, orgId, environment } = useSessionData();
	const shouldShowRovo = fg('rovo_use_rovo_llm_enabled_check') ? isRovoLLMEnabled : isRovoEnabled;

	let onboardingActionCardsToUse = getOnboardingActionsData();
	let headingMessage = i18n.heading;

	if (expVal('cfind-5225-updated-home-onboarding-tiles', 'isEnabled', false)) {
		headingMessage = i18nCfind5225.heading;
		onboardingActionCardsToUse = [
			OnboardingActionCardsCFIND5225[0], // setupTeamSpace
			OnboardingActionCardsCFIND5225[1], // importWork
			...(shouldShowRovo ? [OnboardingActionCardsCFIND5225[2]] : []), // searchApps
			OnboardingActionCardsCFIND5225[3], // draftIdeas
			...(!shouldShowRovo ? [OnboardingActionCardsCFIND5225[4]] : []), // brainstormOnWhiteboard
		];
	}

	const { actions, dismiss, complete, hasDismissedOnboardingActions } =
		usePersonalizedTemplateOnboardingActions(onboardingActionCardsToUse, onboardingStateData);
	const actionsMap = Object.fromEntries(actions.map((action) => [action.id, action]));

	const handleClick = useCallback(
		(id: string, event: React.MouseEvent<Element> | React.KeyboardEvent<Element>) => {
			complete?.(id);
			if (id === 'createTeamProfile') {
				setCreateTeamModalOpen(true);
			}
			if (id === 'setupTeamSpace') {
				handleSetupTeamSpaceClick(event);
			}
			if (id === 'importWork') {
				importContentActions.setShouldRenderModal(true);
				importContentActions.setShowImportContentModal(true);
			}
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId: 'gettingStartedCardPTV2',
					source: 'cc-onboarding-personalized-templates-v2',
					attributes: {
						onboardingAction: id,
						onboardingPeriodDay,
						isXflow,
					},
				},
			}).fire();
		},
		[
			complete,
			createAnalyticsEvent,
			handleSetupTeamSpaceClick,
			onboardingPeriodDay,
			isXflow,
			importContentActions,
		],
	);

	const handleDismiss = useCallback(
		(id: string) => {
			dismiss?.(id);
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId: 'dismissGettingStartedCardPTV2',
					source: 'cc-onboarding-personalized-templates-v2',
					attributes: {
						onboardingAction: id,
						onboardingPeriodDay,
						isXflow,
					},
				},
			}).fire();
		},
		[dismiss, createAnalyticsEvent, onboardingPeriodDay, isXflow],
	);

	const handleDismissOnboardingActionCards = useCallback(() => {
		setOnboardingState({
			key: PERSONALIZED_TEMPLATES_V2_ONBOARDING_STATE_KEYS.hasDismissedOnboardingActions,
			value: 'true',
		});
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'dismissPTV2GettingStarted',
				source: 'cc-onboarding-personalized-templates-v2',
				attributes: {
					onboardingPeriodDay,
					isXflow,
				},
			},
		}).fire();
	}, [setOnboardingState, createAnalyticsEvent, onboardingPeriodDay, isXflow]);

	// Generate page and whiteboard creation links
	const pageCreationLink = getButtonHref({
		contentType: 'page',
		source: 'experimentPersonalizedTemplatesV2CreatePage',
		personalSpaceKey,
		openTemplatesGallery: false,
	});

	const whiteboardCreationLink = getButtonHref({
		contentType: 'whiteboard',
		source: 'experimentPersonalizedTemplatesV2CreateWhiteboard',
		personalSpaceKey,
		openTemplatesGallery: false,
	});

	const hrefMap: Record<string, string> = {
		createPage: pageCreationLink,
		brainstormOnWhiteboard: whiteboardCreationLink,
		// using the same page creation link for draftIdeas, since it creates a live doc if it's enabled
		draftIdeas: pageCreationLink,
		searchApps: getRovoLink(orgId, environment) || '',
	};

	const dismissedCardIds = new Set(
		Object.values(actionsMap)
			.filter((action) => action.state === 'dismissed')
			.map((action) => action.id),
	);

	//  Filter out dismissed cards and space creation card if no space create permissions
	const filteredOnboardingActionCards = onboardingActionCardsToUse.filter(
		(card) => !(card.id === 'setupTeamSpace' && !canCreateSpace) && !dismissedCardIds.has(card.id),
	);

	const nonCompletedCards = filteredOnboardingActionCards.filter(
		(card) => actionsMap[card.id]?.state !== 'completed',
	);
	const completedCards = filteredOnboardingActionCards.filter(
		(card) => actionsMap[card.id]?.state === 'completed',
	);
	const sortedOnboardingActionCards = [...nonCompletedCards, ...completedCards];

	if (hasDismissedOnboardingActions || filteredOnboardingActionCards.length === 0) {
		return null;
	}

	return (
		<Box xcss={styles.containerStyles}>
			<Flex xcss={styles.headerStyles} justifyContent="space-between">
				<Heading size="xsmall">
					<FormattedMessage {...headingMessage} />
				</Heading>
				<IconButton
					isTooltipDisabled={false}
					onClick={handleDismissOnboardingActionCards}
					label={formatMessage(i18n.dismissOnboardingLabel)}
					appearance="subtle"
					icon={(iconProps) => <CrossIcon {...iconProps} />}
				/>
			</Flex>
			<Grid xcss={gridStyles} gap="space.200">
				{sortedOnboardingActionCards.map((card) => (
					<GettingStartedCard
						key={card.id}
						image={card.image}
						heading={card.heading}
						description={card.description}
						ctaText={card.ctaText}
						ctaCompletedText={card.ctaCompletedText}
						dismissTooltip={card.dismissTooltip}
						onClick={(event: React.MouseEvent<Element> | React.KeyboardEvent<Element>) =>
							handleClick(card.id, event)
						}
						onDismiss={() => handleDismiss(card.id)}
						hrefLink={hrefMap[card.id]}
						cardCompleted={actionsMap[card.id]?.state === 'completed'}
						hasBorder
						headingSize="xsmall"
					/>
				))}
			</Grid>
			<CreateTeamModal
				isOpen={isCreateTeamModalOpen}
				onClose={() => setCreateTeamModalOpen(false)}
			/>
		</Box>
	);
};
